import * as amplitude from "@amplitude/analytics-browser";
import { useEffect, useRef } from "react";
import { AnalyticsContext } from "./AnalyticsContext";
import { useSelector } from "react-redux";
import { alertTestAccountEvent } from "./testAccountAlert";

const AmplitudeAnalyticsProvider = ({ apiKey, children }) => {
  const userDetails = useSelector((state) => state.userDetails);
  const { email: userEmail = "", fullName, organizationName, intelPaidPlans } = userDetails.userData;
  const initialized = useRef(false);
  const plan = intelPaidPlans?.length > 0 ? "Paid" : "Free";

  // Init once, and only after the email is known so unauthenticated sessions never send events
  useEffect(() => {
    if (!userEmail || initialized.current) return;
    initialized.current = true;
    amplitude.init(apiKey, {
      userId: userEmail,
      defaultTracking: {
        sessions: true,
        pageViews: true,
        formInteractions: false,
        fileDownloads: false,
      },
    });
  }, [apiKey, userEmail]);

  // amplitude.reset() (logout, user switch) clears the user id and init no longer reruns, so set it explicitly
  useEffect(() => {
    if (!userEmail) return;
    amplitude.setUserId(userEmail);
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail) return;
    const identifyEvent = new amplitude.Identify();
    if (fullName) identifyEvent.set("name", fullName);
    if (organizationName) identifyEvent.set("organization", organizationName);
    identifyEvent.set("plan", plan);
    amplitude.identify(identifyEvent);
  }, [userEmail, fullName, organizationName, plan]);

  const amplitudeAnalytics = {
    setUser: (id) => {
      amplitude.setUserId(id);
    },
    trackEvent: (name, data) => {
      if (!userEmail) return;
      alertTestAccountEvent(userEmail, name, data);
      amplitude.track(name, data);
    },
    setUserProperties: (properties) => {
      const identifyEvent = new amplitude.Identify();
      Object.entries(properties).forEach(([key, value]) => {
        identifyEvent.set(key, value);
      });
      amplitude.identify(identifyEvent);
    },
  };

  return <AnalyticsContext.Provider value={amplitudeAnalytics}>{children}</AnalyticsContext.Provider>;
};

export default AmplitudeAnalyticsProvider;
